import { ResizeObserver as Polyfill } from '@juggle/resize-observer';
const ResizeObserver = window.ResizeObserver || Polyfill;

import './countdown.scss';

const numbersHtml = '<div class="column--digits"><div class="column--digits-now">0</div><div class="column--digits-next">1</div></div>',
    _day = 1000 * 60 * 60 * 24,
    _hour = 1000 * 60 * 60,
    _minute = 1000 * 60;

class Countdown {

    constructor(el) {

        this.el = el;
        this.active = false;
        this.width = 0;
        this.resizeRequested = false;
        this.resizeTimeout = null;
        this.columnSettings = el.dataset.columns.toLowerCase();
        this.stage = el.querySelector('.countdown--stage');
        this.liveAlert = el.querySelector('.countdown--live-alert');

        // parse deadline and set to zero if not valid
        this.deadline = new Date(el.dataset.deadline).getTime();
        if(isNaN(this.deadline)) this.deadline = 0;

        // column structure
        this.columns = {
            days: {
                render: this.columnSettings.indexOf('d') >= 0
            },
            hours: {
                render: this.columnSettings.indexOf('h') >= 0
            },
            minutes: {
                render: this.columnSettings.indexOf('m') >= 0
            },
            seconds: {
                render: this.columnSettings.indexOf('s') >= 0
            }
        };

        // get initial remaining values
        let r = this.getRemaining();

        // set digit count for each column
        this.columns.days.digitCount = Math.max(r.days.toString().length, 2);
        this.columns.hours.digitCount = Math.max(r.hours.toString().length, 2);
        this.columns.minutes.digitCount = Math.max(r.minutes.toString().length, 2);
        this.columns.seconds.digitCount = Math.max(r.seconds.toString().length, 2);

        // build and add initial html content
        let c = '',
            nHtml = numbersHtml;
        if(this.columns.days.render) {
            c += '<div class="column column--days">';
            for(let x = 0; x < this.columns.days.digitCount; x++) c+= nHtml;
            c += '<div class="column--label">Days</div></div>';
        }
        if(this.columns.hours.render) {
            c += '<div class="column column--hours">';
            for(let x = 0; x < this.columns.hours.digitCount; x++) c+= nHtml;
            c += '<div class="column--label"><abbr title="Hours">Hrs</abbr></div></div>';
        }
        if(this.columns.minutes.render) {
            c += '<div class="column column--minutes">';
            for(let x = 0; x < this.columns.minutes.digitCount; x++) c+= nHtml;
            c += '<div class="column--label"><abbr title="Minutes">Mins</abbr></div></div>';
        }
        if(this.columns.seconds.render) {
            c += '<div class="column column--seconds">';
            for(let x = 0; x < this.columns.seconds.digitCount; x++) c+= nHtml;
            c += '<div class="column--label"><abbr title="Seconds">Secs</abbr></div></div>';
        }
        this.stage.innerHTML = c;

        // store digits
        if(this.columns.days.render) {
            this.columns.days.digits = [];
            [...this.stage.querySelectorAll('.column--days .column--digits')].forEach((digitElement) => {
                this.columns.days.digits.push(new ColumnDigit(digitElement));
            });
        }
        if(this.columns.hours.render) {
            this.columns.hours.digits = [];
            [...this.stage.querySelectorAll('.column--hours .column--digits')].forEach((digitElement) => {
                this.columns.hours.digits.push(new ColumnDigit(digitElement));
            });
        }
        if(this.columns.minutes.render) {
            this.columns.minutes.digits = [];
            [...this.stage.querySelectorAll('.column--minutes .column--digits')].forEach((digitElement) => {
                this.columns.minutes.digits.push(new ColumnDigit(digitElement));
            });
        }
        if(this.columns.seconds.render) {
            this.columns.seconds.digits = [];
            [...this.stage.querySelectorAll('.column--seconds .column--digits')].forEach((digitElement) => {
                this.columns.seconds.digits.push(new ColumnDigit(digitElement));
            });
        }
        // update columns
        this.update(true);

        // add resizeObserver to adjust the scale of the countdown if too wide for container
        this.ro = new ResizeObserver(entries => {
            for (let entry of entries) {
                const cr = entry.contentRect;
                if(cr.width != this.width) {
                    this.width = Math.round(cr.width);
                    if(!this.resizeRequested) {
                        this.requestResize();
                    }
                }
            }
        });
        this.ro.observe(this.el); // Watch dimension changes wrapping element
    }

    requestResize() {
        clearTimeout(this.resizeTimeout);
        this.resizeRequested = true;
        this.resizeTimeout = setTimeout(() => {
            this.resize();
        }, 100);
    }

    resize() {
        let stageWidth = this.stage.offsetWidth;
        //console.log(stageWidth + " : " + this.width);
        let scale = stageWidth > this.width ? (this.width / stageWidth) : 1;
        this.el.style.setProperty('--scale', `${scale}`);
        //console.log(stageWidth + ' : ' +  this.width + ' = scale: ' + scale);
        // reset resizeRequested
        this.resizeRequested = false;
        // make active?
        if(!this.active) {
            this.el.classList.add('active');
            this.active = true;
        }
    }

    update(initial) {
        // initial = boolean : is this the first update (no animation)?
        // calculate the differences between now and deadline
        let r = this.getRemaining();
        // update accessible content with all relevant values
        let alertContent = 'Counting down: ';
        if(r.days > 0 && this.columns.days.render) alertContent += (r.days + ' days, ');
        if((r.days > 0 || r.hours > 0) && this.columns.hours.render) alertContent += (r.hours + ' hours, ');
        if((r.days > 0 || r.hours > 0 || r.minutes > 0) && this.columns.minutes.render) alertContent += (r.minutes + ' minutes, ');
        if(this.columns.seconds.render) alertContent += (r.seconds + ' seconds.');
        this.liveAlert.innerHTML = alertContent;
        // update digits?
        if(this.columns.days.render) this.updateDigits(r.days, this.columns.days.digitCount, this.columns.days.digits, initial);
        if(this.columns.hours.render) this.updateDigits(r.hours, this.columns.hours.digitCount, this.columns.hours.digits, initial);
        if(this.columns.minutes.render) this.updateDigits(r.minutes, this.columns.minutes.digitCount, this.columns.minutes.digits, initial);
        if(this.columns.seconds.render) this.updateDigits(r.seconds, this.columns.seconds.digitCount, this.columns.seconds.digits, initial);

        // trigger again in 1 second (if still needed)
        if(!r.complete) {
        setTimeout(()=> {
                this.update(false);
            },1000);
        }
    }

    getRemaining() {
        // calculate the differences between now and deadline
        let now = new Date().getTime(),
            r = this.deadline - now;
        if(r < 0) r = 0;
        // get column values
        let _days = Math.floor(r / _day),
            _hours = this.columns.days.render ? Math.floor((r % _day) / _hour) : Math.floor(r / _hour),
            _minutes = this.columns.hours.render ? Math.floor((r % _hour) / _minute) : Math.floor(r / _minute),
            _seconds = this.columns.minutes.render ? Math.floor((r % _minute) / 1000) : Math.floor(r / 1000);
        // return as object
        return {
            days: _days,
            hours: _hours,
            minutes: _minutes,
            seconds: _seconds,
            complete: r == 0
        }
    }

    updateDigits(total, digitCount, digits, initial) {
        // total = int : amount remaining
        // digitCount = int : number of digits in the column (3 or 2)
        // digits = array : of digit dom elements
        // initial = boolean : is this the first update (no animation)?
        // get total as a string and match number of digits
        let totalString = total.toString();
        while(totalString.length < digitCount) totalString = '0' + totalString;
        // loop through digits
        for(let x = 0; x < digitCount; x++) {
            let digitNumber = totalString.charAt(x);
            digits[x].update(digitNumber, initial);
        }
    }

}

class ColumnDigit {

    constructor(el) {
        this.el = el;
        this.timeout = null;
        this.nowObj = this.el.querySelector('.column--digits-now');
        this.nextObj = this.el.querySelector('.column--digits-next');
        this.prev = '0';
    }

    update(digitNumber, initial) {
        if(initial) {
            // just put number into 'now' element
            this.nowObj.innerHTML = digitNumber;
        } else if(digitNumber != this.prev) {
            // put number into 'next' element
            this.nextObj.innerHTML = digitNumber;
            // animate
            this.el.classList.add('shift');
            this.timeout = setTimeout(() => {
                this.nowObj.innerHTML = digitNumber;
                this.el.classList.remove('shift');
                clearTimeout(this.timeout);
                this.timeout = null;
            }, 900);
        }
        this.prev = digitNumber;
    }
}

// export the default function to create
export function createFrom(wrappers) {
    wrappers.forEach((node) => {
        // if node is an element
        if (node.nodeType === 1) {
            new Countdown(node);
        }
    });
}